import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, from, of } from "rxjs";
import {
  map,
  catchError,
  tap,
  first,
  distinctUntilChanged,
} from "rxjs/operators";
import {
  User,
  Session,
  AuthChangeEvent,
  AuthError,
} from "@supabase/supabase-js";
import { SupabaseService } from "./supabase.service";
import { Router } from "@angular/router";
import posthog from "posthog-js";

@Injectable({
  providedIn: "root",
})
export class AuthService {
  private currentUserSubject = new BehaviorSubject<User | null>(null);
  public currentUser$ = this.currentUserSubject
    .asObservable()
    .pipe(
      distinctUntilChanged(
        (prevUser, currUser) => prevUser?.id === currUser?.id
      )
    );
  private currentSessionSubject = new BehaviorSubject<Session | null>(null);
  public currentSession$ = this.currentSessionSubject.asObservable().pipe(
    distinctUntilChanged((prevSession, currSession) => {
      if (prevSession === null && currSession === null) return true;
      if (prevSession === null || currSession === null) return false;
      return (
        prevSession.access_token === currSession.access_token &&
        prevSession.user.id === currSession.user.id
      );
    })
  );

  // Observable to control login modal visibility
  private showLoginModalSubject = new BehaviorSubject<boolean>(false);
  public showLoginModal$ = this.showLoginModalSubject.asObservable();

  // Observable to signal when initial auth check is done
  private authInitializedSubject = new BehaviorSubject<boolean>(false);
  public authInitialized$ = this.authInitializedSubject.asObservable();

  // User profile from public.users (includes profile_picture_url)
  private userProfileSubject = new BehaviorSubject<any | null>(null);
  public userProfile$ = this.userProfileSubject.asObservable();

  // Add these properties for managing redirect path and pending actions
  private redirectPath: string | null = null;
  private pendingActionAfterLogin: string | null = null;
  private pendingApplicationUrl: string | null = null;

  constructor(
    private supabaseService: SupabaseService,
    private router: Router
  ) {
    // Listen to Supabase auth changes
    this.supabaseService
      .getClient()
      .auth.onAuthStateChange((event, session) => {
        const previousSession = this.currentSessionSubject.value;

        // Condition to prevent logging for redundant INITIAL_SESSION events
        const isRedundantInitialSession =
          event === "INITIAL_SESSION" &&
          previousSession &&
          session &&
          previousSession.access_token === session.access_token &&
          previousSession.user.id === session.user.id;

        if (!isRedundantInitialSession) {
          // REMOVED: console.log(...)
        }

        this.currentSessionSubject.next(session);
        this.currentUserSubject.next(session?.user ?? null);

        // Fetch user profile from public.users when session changes
        if (session?.user?.id) {
          this.fetchUserProfile(session.user.id);
          // Identify user in PostHog when they log in
          this.identifyUserInPostHog(session.user);
        } else {
          this.userProfileSubject.next(null);
          // Reset PostHog when user logs out
          if (typeof posthog !== 'undefined') {
            posthog.reset();
          }
        }

        if (session && !previousSession) {
          // REMOVED: console.log("AuthService: New session detected (user logged in).");
          // NOTE: Redirect handling removed from here to prevent race condition
          // The redirect is now handled exclusively in app.component.ts onLoginComplete
        } else if (!session && previousSession) {
          // REMOVED: console.log("AuthService: Session ended (user logged out).");
        } else if (
          session &&
          previousSession &&
          session.user.id === previousSession.user.id
        ) {
          // REMOVED: console.log("AuthService: Session refreshed or token updated, no redirect needed.");
        } else {
          // REMOVED: console.log("AuthService: onAuthStateChange - no specific login/logout action detected for redirect.");
        }

        if (!this.authInitializedSubject.value) {
          // REMOVED: console.log("AuthService: Auth system initialized.");
          this.authInitializedSubject.next(true);
        }
      });

    // Initial check for session - Supabase client handles this automatically now
    // based on its persisted session state, so this .then block might be redundant
    // unless you need immediate synchronous access *during construction* which is rare.
    // Consider removing this block if initial state is handled sufficiently by the
    // BehaviorSubject default (null) and the onAuthStateChange listener.
    this.supabaseService
      .getClient()
      .auth.getSession()
      .then(({ data, error }) => {
        if (error) {
          // Ensure initialization completes even if getSession fails
          if (!this.authInitializedSubject.value) {
            this.authInitializedSubject.next(true);
          }
        }
      })
      .catch((error) => {
        // Ensure initialization completes even if getSession fails
        if (!this.authInitializedSubject.value) {
          this.authInitializedSubject.next(true);
        }
      });
  }

  /**
   * Show the login modal
   */
  openLoginModal(): void {
    this.showLoginModalSubject.next(true);
  }

  /**
   * Hide the login modal
   */
  closeLoginModal(): void {
    this.showLoginModalSubject.next(false);
  }

  /**
   * Email OTP authentication - sends a code instead of a magic link
   */
  async signInWithEmail(
    email: string,
    captchaToken: string | undefined
  ): Promise<{ data: any; error: any }> {
    try {
      // Basic email validation
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!emailRegex.test(email)) {
        return {
          data: null,
          error: {
            message:
              "Invalid email format. Please provide a valid email address.",
          },
        };
      }

      const client = this.supabaseService.getClient();

      // Send OTP code via email, including the captcha token
      const response = await client.auth.signInWithOtp({
        email,
        options: {
          shouldCreateUser: true,
          captchaToken: captchaToken,
        },
      });

      if (response.error) {
        // Check if this is a rate limiting error
        if (
          response.error.message &&
          response.error.message.includes("security purposes") &&
          response.error.message.includes("seconds")
        ) {
          return {
            data: null,
            error: {
              message: "Please wait a moment before requesting another code.",
              isRateLimited: true,
            },
          };
        }

        // Check for specific error types and return clear messages
        if (
          response.error.message &&
          response.error.message.includes("user not found")
        ) {
          return {
            data: null,
            error: {
              message:
                "This email is not registered. Please contact an administrator to create your account first.",
            },
          };
        } else if (
          response.error.message &&
          response.error.message.includes("Signups not allowed")
        ) {
          return {
            data: null,
            error: {
              message:
                "New signups are not enabled. Please contact an administrator to create your account first.",
            },
          };
        }
      } else {
        // REMOVED: console.log("OTP code email sent successfully");
      }

      return response;
    } catch (error) {
      // REMOVED: console.error("Unexpected error sending OTP:", error);
      return {
        data: null,
        error: {
          message: "An unexpected error occurred. Please try again later.",
        },
      };
    }
  }

  /**
   * Verify the OTP code entered by the user
   */
  async verifyOtp(
    email: string,
    token: string
  ): Promise<{ data: any; error: any }> {
    try {
      // REMOVED: console.log("Verifying OTP for:", email);

      const client = this.supabaseService.getClient();
      const response = await client.auth.verifyOtp({
        email,
        token,
        type: "email",
      });

      if (response.error) {
        // REMOVED: console.error("OTP verification error:", response.error);

        // Check for specific error messages
        if (
          response.error.message &&
          response.error.message.includes("Invalid")
        ) {
          return {
            data: null,
            error: {
              message: "Invalid verification code. Please try again.",
            },
          };
        } else if (
          response.error.message &&
          response.error.message.includes("expired")
        ) {
          return {
            data: null,
            error: {
              message:
                "Verification code has expired. Please request a new one.",
            },
          };
        }
      } else {
        // REMOVED: console.log("OTP verification successful");
        // No need to manually save session, Supabase handles it
        // await this.supabaseService.saveSession();
      }

      return response;
    } catch (error) {
      // REMOVED: console.error("Unexpected error verifying OTP:", error);
      return {
        data: null,
        error: {
          message: "An unexpected error occurred. Please try again later.",
        },
      };
    }
  }

  /**
   * Legacy/compatibility methods - should also accept optional token
   */
  async createUser(
    email: string,
    captchaToken?: string | undefined
  ): Promise<{ data: any; error: any }> {
    return this.signInWithEmail(email, captchaToken);
  }

  async signInWithOtp(
    email: string,
    captchaToken?: string | undefined
  ): Promise<{ data: any; error: any }> {
    return this.signInWithEmail(email, captchaToken);
  }

  async signInWithMagicLink(
    email: string,
    captchaToken?: string | undefined
  ): Promise<{ data: any; error: any }> {
    return this.signInWithEmail(email, captchaToken);
  }

  /**
   * Check if user is currently authenticated based on Supabase session.
   */
  isAuthenticated(): boolean {
    // Check the latest value from the session subject
    const authenticated = !!this.currentSessionSubject.value;
    return authenticated;
  }

  /**
   * Check if the auth service has completed its initialization
   */
  isInitialized(): boolean {
    return this.authInitializedSubject.value;
  }

  /**
   * Get the current user from the Supabase session.
   */
  getCurrentUser(): User | null {
    return this.currentUserSubject.value;
  }

  /**
   * Refresh user profile from public.users (e.g. after profile picture update).
   * Call this after saving profile so the nav avatar updates without page refresh.
   */
  refreshUserProfile(): void {
    const userId = this.getCurrentUser()?.id;
    if (userId) {
      this.fetchUserProfile(userId);
    }
  }

  /**
   * Fetch user profile from public.users (includes profile_picture_url)
   */
  private async fetchUserProfile(userId: string): Promise<void> {
    try {
      const { data, error } = await this.supabaseService
        .getClient()
        .from('users')
        .select('id, email, first_name, last_name, display_name, profile_picture_url')
        .eq('id', userId)
        .single();

      if (error) {
        console.error('Error fetching user profile:', error);
        this.userProfileSubject.next(null);
        return;
      }

      this.userProfileSubject.next(data);
      
      // Update PostHog with profile data after fetching
      if (data && typeof posthog !== 'undefined') {
        posthog.people.set({
          email: data.email,
          name: data.display_name || `${data.first_name || ''} ${data.last_name || ''}`.trim(),
          first_name: data.first_name,
          last_name: data.last_name,
        });
      }
    } catch (err) {
      console.error('Error fetching user profile:', err);
      this.userProfileSubject.next(null);
    }
  }

  /**
   * Identify user in PostHog for analytics tracking
   */
  private identifyUserInPostHog(user: User): void {
    if (typeof posthog === 'undefined') {
      return;
    }

    // Identify the user with their Supabase user ID
    posthog.identify(user.id, {
      email: user.email,
      // Additional properties will be set when user profile is fetched
    });
  }

  /**
   * Get the current JWT access token from the Supabase session.
   * Returns null if the user is not authenticated.
   */
  getAccessToken(): string | null {
    return this.currentSessionSubject.value?.access_token ?? null;
  }

  /**
   * Logout the user using Supabase Auth.
   */
  logout(): Observable<{ error: AuthError | null }> {
    // REMOVED: console.log("Logging out user");
    return from(this.supabaseService.getClient().auth.signOut()).pipe(
      map((response) => {
        // REMOVED: console.log("signOut response:", response);
        // State is updated via onAuthStateChange
        return { error: response.error };
      }),
      catchError((error) => {
        // REMOVED: console.error("signOut error:", error);
        return of({ error }); // Return error within the observable stream
      })
    );
  }

  /**
   * Sign in using an OAuth provider (e.g., Google, GitHub)
   */
  async signInWithProvider(provider: "google" | "github"): Promise<{
    data: any;
    error: any;
  }> {
    try {
      // REMOVED: console.log(`Attempting to sign in with ${provider}`);
      const client = this.supabaseService.getClient();

      // Initiate OAuth flow
      const response = await client.auth.signInWithOAuth({
        provider,
        options: {
          // Redirect back to the current page after Supabase processes OAuth response
          redirectTo: window.location.href,
        },
      });

      if (response.error) {
        // REMOVED: console.error(`Error signing in with ${provider}:`, response.error);
      }

      // Supabase handles the redirect, so there's usually no direct data return here
      // The session will be updated via the onAuthStateChange listener
      return response;
    } catch (error) {
      // REMOVED: console.error(`Unexpected error signing in with ${provider}:`, error);
      return {
        data: null,
        error: {
          message: `An unexpected error occurred while signing in with ${provider}. Please try again later.`,
        },
      };
    }
  }

  // Add the following methods for redirect and pending action management

  setRedirectPath(path: string): void {
    this.redirectPath = path;
    // Persist to sessionStorage for OAuth flows that reload the page
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem('auth_redirect_path', path);
    }
  }

  getRedirectPath(): string | null {
    // Try memory first, then sessionStorage
    let pathToReturn = this.redirectPath;
    
    if (!pathToReturn && typeof sessionStorage !== 'undefined') {
      pathToReturn = sessionStorage.getItem('auth_redirect_path');
    }
    
    // Clear from both locations after retrieving
    this.redirectPath = null;
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem('auth_redirect_path');
    }
    
    return pathToReturn;
  }

  clearRedirectPath(): void {
    this.redirectPath = null;
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem('auth_redirect_path');
    }
    // REMOVED: console.log("AuthService: Redirect path cleared.");
  }

  setPendingActionAfterLogin(action: string): void {
    this.pendingActionAfterLogin = action;
    // REMOVED: console.log("AuthService: Pending action after login set to:", this.pendingActionAfterLogin);
  }

  getPendingActionAfterLogin(): string | null {
    const action = this.pendingActionAfterLogin;
    if (action) {
      this.pendingActionAfterLogin = null; // Clear after retrieving
      // REMOVED: console.log("AuthService: Pending action after login retrieved and cleared:", action);
    }
    return action;
  }

  // Handle job application with authentication check
  handleJobApplication(applicationUrl: string, jobData?: any): boolean {
    if (this.isAuthenticated()) {
      // User is logged in, allow application
      window.open(applicationUrl, "_blank", "noopener,noreferrer");
      return true;
    } else {
      // User is not logged in, store application URL and show login modal
      this.pendingApplicationUrl = applicationUrl;
      this.setPendingActionAfterLogin("apply_to_job");
      this.openLoginModal();
      return false;
    }
  }

  // Process pending application after successful login
  processPendingApplication(): void {
    const pendingAction = this.getPendingActionAfterLogin();
    if (pendingAction === "apply_to_job" && this.pendingApplicationUrl) {
      const urlToOpen = this.pendingApplicationUrl;
      this.pendingApplicationUrl = null; // Clear the pending URL

      // Small delay to ensure login modal closes smoothly
      setTimeout(() => {
        window.open(urlToOpen, "_blank", "noopener,noreferrer");
      }, 500);
    }
  }

  // Clear pending application data
  clearPendingApplicationData(): void {
    this.pendingApplicationUrl = null;
  }

  // Removed methods:
  // - checkAndCreateUser (Supabase handles user creation)
  // - sendVerificationCode (Replaced by signInWithOtp)
  // - verifyCode (Replaced by verifyOtp)
  // - updateCurrentUser (State managed by Supabase session & onAuthStateChange)
  // - getToken (Supabase manages session tokens internally)
}
